import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  ListItemText,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { SystemStyleObject } from '@mui/system';
import { Theme } from '@mui/material/styles';

import { DealerSearchInput } from 'app/components/ReusableUIComponents/LowLevelComponents/DealerSearchInput';
import {
  HeaderDropdownMAIQ,
  HeaderDropdownMenuItemMAIQ,
  HEADER_DROPDOWN_LIGHT_TRIGGER_SX,
} from 'app/components/ReusableUIComponents/LowLevelComponents/HeaderDropdownMAIQ';
import ButtonMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/buttons/ButtonMAIQ';

export type HeaderSearchableSelectOption = {
  value: string;
  label: string;
  secondaryLabel?: string;
};

type HeaderSearchableSelectBaseProps = {
  options: HeaderSearchableSelectOption[];
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  emptyLabel?: string;
  searchPlaceholder?: string;
  emptySearchLabel?: string;
  emptyOptionsLabel?: string;
  emptySelectionLabel?: string;
  isOptionDisabled?: (option: HeaderSearchableSelectOption) => boolean;
  disabledOptionTooltip?: string;
  className?: string;
  id?: string;
  triggerWidth?: number;
  menuMinWidth?: number;
  'aria-label'?: string;
};

export type HeaderSearchableSelectSingleProps = HeaderSearchableSelectBaseProps & {
  multiple?: false;
  value: string;
  values?: never;
  onChange: (value: string) => void;
  showChips?: never;
  requireConfirm?: never;
};

export type HeaderSearchableSelectMultipleProps = HeaderSearchableSelectBaseProps & {
  multiple: true;
  value?: never;
  values: string[];
  onChange: (values: string[]) => void;
  showChips?: boolean;
  requireConfirm?: boolean;
};

export type HeaderSearchableSelectMAIQProps =
  | HeaderSearchableSelectSingleProps
  | HeaderSearchableSelectMultipleProps;

const DEFAULT_TRIGGER_WIDTH = 280;

const getConstrainedTriggerSx = (
  width: number,
): SystemStyleObject<Theme> => ({
  ...HEADER_DROPDOWN_LIGHT_TRIGGER_SX,
  width,
  maxWidth: width,
  flexShrink: 0,
});

const filterOptions = (
  options: HeaderSearchableSelectOption[],
  searchTerm: string,
): HeaderSearchableSelectOption[] => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return options;
  return options.filter(option => {
    const haystack = [option.label, option.secondaryLabel]
      .filter(Boolean)
      .map(value => String(value).toLowerCase());
    return haystack.some(value => value.includes(term));
  });
};

const getOptionByValue = (
  options: HeaderSearchableSelectOption[],
  value: string,
): HeaderSearchableSelectOption | undefined =>
  options.find(option => option.value === value);

export function HeaderSearchableSelectMAIQ(props: HeaderSearchableSelectMAIQProps) {
  const {
    options,
    disabled = false,
    loading = false,
    loadingLabel = 'Loading...',
    emptyLabel = 'Select…',
    searchPlaceholder = 'Search…',
    emptySearchLabel = 'No options found',
    emptyOptionsLabel = 'No options available',
    emptySelectionLabel = 'None selected',
    isOptionDisabled,
    disabledOptionTooltip,
    className,
    id = 'header-searchable-select',
    triggerWidth = DEFAULT_TRIGGER_WIDTH,
    menuMinWidth = 320,
    'aria-label': ariaLabel,
  } = props;

  const committedValues =
    props.multiple === true ? props.values || [] : [];
  const showChips =
    props.multiple === true ? props.showChips !== false : false;
  const requireConfirm =
    props.multiple === true ? props.requireConfirm !== false : false;
  const multiple = props.multiple === true;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [draftValues, setDraftValues] = useState<string[]>([]);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open && multiple) {
      setDraftValues(committedValues);
    }
  }, [committedValues, multiple, open]);

  const filteredOptions = useMemo(
    () => filterOptions(options, searchTerm),
    [options, searchTerm],
  );

  const selectedOptions = useMemo(
    () =>
      committedValues
        .map(value => getOptionByValue(options, value))
        .filter((option): option is HeaderSearchableSelectOption =>
          Boolean(option),
        ),
    [committedValues, options],
  );

  const activeValues = multiple
    ? requireConfirm
      ? draftValues
      : committedValues
    : [];

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled || loading) return;
    if (multiple && requireConfirm) {
      setDraftValues(committedValues);
    }
    setAnchorEl(event.currentTarget);
    setSearchTerm('');
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm('');
    if (multiple && requireConfirm) {
      setDraftValues(committedValues);
    }
  };

  const commitSingleValue = (value: string) => {
    if (props.multiple === true) {
      return;
    }
    props.onChange(value);
  };

  const commitMultipleValues = (values: string[]) => {
    if (props.multiple !== true) {
      return;
    }
    props.onChange(values);
  };

  const handleSingleSelect = (option: HeaderSearchableSelectOption) => {
    if (props.multiple === true) return;
    if (isOptionDisabled?.(option)) return;
    commitSingleValue(option.value);
    handleClose();
  };

  const handleMultiToggle = (option: HeaderSearchableSelectOption) => {
    if (props.multiple !== true) return;

    const alreadySelected = activeValues.includes(option.value);
    if (!alreadySelected && isOptionDisabled?.(option)) return;

    const next = alreadySelected
      ? activeValues.filter(value => value !== option.value)
      : [...activeValues, option.value];

    if (requireConfirm) {
      setDraftValues(next);
      return;
    }

    commitMultipleValues(next);
  };

  const handleApply = () => {
    if (props.multiple !== true || !requireConfirm) return;
    commitMultipleValues(draftValues);
    setAnchorEl(null);
    setSearchTerm('');
  };

  const handleChipDelete = (value: string) => {
    if (props.multiple !== true || disabled || loading) return;
    commitMultipleValues(committedValues.filter(item => item !== value));
  };

  const selectedSingleOption =
    props.multiple !== true && props.value
      ? getOptionByValue(options, props.value)
      : undefined;

  const triggerLabel = loading ? (
    <>
      <CircularProgress size={16} sx={{ mr: 1 }} />
      {loadingLabel}
    </>
  ) : multiple ? (
    emptyLabel
  ) : selectedSingleOption ? (
    selectedSingleOption.secondaryLabel
      ? `${selectedSingleOption.label} (${selectedSingleOption.secondaryLabel})`
      : selectedSingleOption.label
  ) : (
    emptyLabel
  );

  const dropdown = (
    <HeaderDropdownMAIQ
      id={id}
      className={multiple && showChips ? undefined : className}
      label={triggerLabel}
      richLabel={loading}
      open={open}
      anchorEl={anchorEl}
      onOpen={handleOpen}
      onClose={handleClose}
      disabled={disabled || loading}
      minWidth={triggerWidth}
      menuMinWidth={menuMinWidth}
      menuMaxHeight={multiple && requireConfirm ? 440 : 400}
      menuVariant="dealer"
      triggerSx={getConstrainedTriggerSx(triggerWidth)}
      aria-label={ariaLabel || emptyLabel}
    >
      <DealerSearchInput
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder={searchPlaceholder}
      />
      <Box
        sx={{
          maxHeight: multiple ? 280 : 320,
          overflowY: 'auto',
        }}
      >
        {filteredOptions.length > 0 ? (
          filteredOptions.map(option => {
            const selected = multiple
              ? activeValues.includes(option.value)
              : props.value === option.value;
            const optionDisabled =
              Boolean(isOptionDisabled?.(option)) && !selected;
            const showDisabledTooltip =
              optionDisabled && Boolean(disabledOptionTooltip);

            const menuItem = (
              <HeaderDropdownMenuItemMAIQ
                key={option.value}
                selected={selected}
                disabled={optionDisabled}
                onClick={() =>
                  multiple
                    ? handleMultiToggle(option)
                    : handleSingleSelect(option)
                }
              >
                <ListItemText
                  sx={{ minWidth: 0 }}
                  primary={
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: optionDisabled ? 'text.disabled' : 'inherit',
                      }}
                    >
                      {option.label}
                    </Typography>
                  }
                  secondary={option.secondaryLabel}
                />
                {selected ? (
                  <CheckIcon
                    sx={{ fontSize: 18, color: 'primary.main', ml: 1 }}
                  />
                ) : null}
              </HeaderDropdownMenuItemMAIQ>
            );

            if (!showDisabledTooltip) {
              return menuItem;
            }

            return (
              <Tooltip
                key={option.value}
                title={disabledOptionTooltip}
                placement="left"
              >
                <span>{menuItem}</span>
              </Tooltip>
            );
          })
        ) : searchTerm ? (
          <MenuItem disabled>
            <ListItemText primary={emptySearchLabel} />
          </MenuItem>
        ) : (
          <MenuItem disabled>
            <ListItemText primary={emptyOptionsLabel} />
          </MenuItem>
        )}
      </Box>

      {multiple && requireConfirm ? (
        <>
          <Divider sx={{ my: 1 }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              px: 1.5,
              pb: 1.5,
            }}
            onMouseDown={event => event.stopPropagation()}
            onClick={event => event.stopPropagation()}
          >
            <ButtonMAIQ variant="outlined" onClick={handleClose}>
              Cancel
            </ButtonMAIQ>
            <ButtonMAIQ
              variant="contained"
              color="primary"
              onClick={handleApply}
              sx={{ color: '#fff' }}
            >
              Apply
            </ButtonMAIQ>
          </Box>
        </>
      ) : null}
    </HeaderDropdownMAIQ>
  );

  if (!multiple || !showChips) {
    return dropdown;
  }

  return (
    <Box
      className={className}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      {selectedOptions.length > 0 ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {selectedOptions.map(option => (
            <Chip
              key={option.value}
              label={option.label}
              title={
                option.secondaryLabel
                  ? `${option.label} (${option.secondaryLabel})`
                  : option.label
              }
              onDelete={
                disabled || loading
                  ? undefined
                  : () => handleChipDelete(option.value)
              }
              deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
              size="small"
              sx={{
                maxWidth: 220,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          ))}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {emptySelectionLabel}
        </Typography>
      )}
      {dropdown}
    </Box>
  );
}

export default HeaderSearchableSelectMAIQ;
